import { Colors } from "@blueprintjs/core";
import { useNavigate } from "react-router";

import { Box, Flex } from "@/components/layout/flexbox";
import { roleplayRankStyle } from "@/components/pages_logged_in/program/components/program-details/const";
import {
  ComponentResponseScenario,
  sortScenariosByDifficulty,
} from "@/components/pages_logged_in/roleplays/utils";
import { DateTimeDisplay } from "@/components/pieces/DateTimeDisplay";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { RankIcon } from "@/components/pieces/roleplays/RankIcon";
import { Avatar } from "@/components/pieces/users/Avatar";
import { getUser } from "@/components/session/JwtTokenManager";
import { CARD_CLASSES, MIDTEXT_COLOR, SUBTEXT_COLOR } from "@/css/constants";
import { ProgramComponentResponseQuery, RoleplayRank } from "@/graphql";
import { NonNullableArrayItem } from "@/types/utils";
import { ensureNonNullArray } from "@/utils/array";

type ResponseRoleplaySession = NonNullableArrayItem<
  NonNullableArrayItem<
    NonNullable<
      ProgramComponentResponseQuery["programComponentResponse"]
    >["roleplayScenarios"]
  >["roleplaySessions"]
>;
interface SessionProps {
  session: ResponseRoleplaySession;
  onClick: () => void;
  canViewSessionDetail: boolean;
}

function Session({ session, onClick, canViewSessionDetail }: SessionProps) {
  const { score, createdAt, archivedAt, formFieldCount, correctAnswerCount } =
    session;

  const rank = session.rank ?? RoleplayRank.Unranked;
  const rankIcon = roleplayRankStyle[rank].rankIcon;

  return (
    <Clickable
      onClick={canViewSessionDetail ? onClick : () => {}}
      cursor={canViewSessionDetail ? "pointer" : "default"}
    >
      {/* @ts-ignore */}
      <Flex
        p={16}
        alignItems="center"
        justifyContent="space-between"
        className="bas round-corners"
        fontSize={14}
        css={{
          "&:hover": {
            borderColor: canViewSessionDetail && "#3788EE",
          },
        }}
      >
        <Flex alignItems="center">
          <RankIcon rank={rankIcon} size={24} />
          <Box ml={10} fontWeight={700}>
            {score}% Score {archivedAt ? " (Archived)" : ""}
          </Box>
          {formFieldCount && formFieldCount > 0 ? (
            <Box ml={10} fontWeight={400} color={SUBTEXT_COLOR}>
              {correctAnswerCount}/{formFieldCount} quiz questions answered
              correctly
            </Box>
          ) : null}
        </Flex>
        <Box>
          <DateTimeDisplay
            time={createdAt}
            formatString="MMM d, yyyy 'at' h:mm a"
          />
        </Box>
      </Flex>
    </Clickable>
  );
}

interface ScenariosProps {
  scenario: ComponentResponseScenario;
  isLead: boolean;
}

function Scenarios({ scenario, isLead }: ScenariosProps) {
  const { name, shortDesc, personaPhotoUrl, roleplaySessions, slug } = scenario;
  const navigate = useNavigate();
  const redirectToSession = (sessionUUID: string) => {
    navigate(`/scenarios/${slug}/${sessionUUID}`);
  };

  const user = getUser();
  const sessions = ensureNonNullArray(roleplaySessions);

  return (
    <Box
      px={[16, 24]}
      py={[16, 24]}
      flex={1}
      className={CARD_CLASSES}
      bg={Colors.WHITE}
    >
      <Flex>
        <Avatar
          user={{
            name: name,
            photo: personaPhotoUrl,
          }}
          size={64}
        />
        <Flex flexDirection="column" ml={3} justifyContent="center">
          <Box fontSize={18} fontWeight={600}>
            {name}
          </Box>
          <Box fontSize={16} color={MIDTEXT_COLOR}>
            {shortDesc}
          </Box>
        </Flex>
      </Flex>
      <Box className="bbs" my={32} />
      {sessions.length > 0 ? (
        <Box>
          <Box fontWeight={600} fontSize={16} mb={3}>
            Practice Sessions
          </Box>

          <Box>
            {sessions.map((session, index) => {
              const isSessionOwner = session?.user?.id === user?.id;
              const canViewSessionDetail =
                (isLead || isSessionOwner) && !session?.archivedAt;

              return (
                <Box
                  key={`roleplay-session-${session?.id}`}
                  mt={index == 0 ? 0 : 16}
                >
                  <Session
                    session={session}
                    onClick={() => {
                      redirectToSession(session?.uuid ?? "");
                    }}
                    canViewSessionDetail={canViewSessionDetail}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : (
        <Box fontSize={14}>No practice sessions yet</Box>
      )}
    </Box>
  );
}

interface ProgramRoleplayResponseProps {
  roleplayScenarios: ComponentResponseScenario[];
  isLead: boolean;
}

export function ProgramRoleplayResponse({
  roleplayScenarios = [],
  isLead = false,
}: ProgramRoleplayResponseProps) {
  const scenarios = sortScenariosByDifficulty(roleplayScenarios);
  return (
    <Box>
      {scenarios.map((scenario, index) => (
        <Box mt={index > 0 ? 24 : 0}>
          <Scenarios scenario={scenario} isLead={isLead} />
        </Box>
      ))}
    </Box>
  );
}
